import { useEffect, useRef } from 'react';

import { i18n } from '@modernloop/i18n';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { API_URL } from 'src/utils/api';

const PING_URL = `${API_URL}/ping`;
const POLL_INTERVAL_MS = 30_000;

/**
 * Periodically pings the backend health endpoint and shows a persistent toast
 * when the connection is lost, dismissing it automatically on recovery.
 *
 * Mount this once at the app root (inside SnackbarProvider).
 */
export function useConnectionMonitor() {
  const snackbarKeyRef = useRef<ReturnType<typeof enqueueSnackbar> | null>(null);
  const isDownRef = useRef(false);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect owns timer lifecycle after render and cleanup.
  useEffect(() => {
    const check = async () => {
      try {
        await fetch(PING_URL, { method: 'GET', cache: 'no-store' });
        if (isDownRef.current) {
          isDownRef.current = false;
          if (snackbarKeyRef.current !== null) {
            closeSnackbar(snackbarKeyRef.current);
            snackbarKeyRef.current = null;
          }
        }
      } catch {
        if (!isDownRef.current) {
          isDownRef.current = true;
          snackbarKeyRef.current = enqueueSnackbar(i18n.t('common:shared.connection.reconnecting'), {
            variant: 'warning',
            persist: true,
            preventDuplicate: true,
          });
        }
      }
    };

    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
}
